import React, { useEffect, useState } from "react"
import { Bounce, toast } from "react-toastify"
import SearchInput from "../../components/Listing/SearchInput"
import FilterDropdown from "../../components/Listing/FilterDropdown"
import Pagination from "../../components/Listing/Pagination"
import { AvatarGroup } from "../../components/DashboardComponents/AvatarGroup"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../components/OnboardingComponents/CustomIconButton"
import EmptyState from "../../components/Common/EmptyState"
import TruncatedText from "../../components/Common/TruncatedText"
import DeleteConfirmation from "../../components/Common/DeleteConfirmation"
import { ICONS } from "../../constants/icons"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import {
  ARCHIVED_PROJECTS_PAGE_SIZE,
  ARCHIVED_SORT_OPTIONS,
  fetchArchivedProjects,
  permanentlyDeleteArchivedProject,
  restoreArchivedProject,
} from "./profileArchivedProjects"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const SKELETON_ROWS = 5
const MAX_VISIBLE_CATEGORIES = 2

const getRandomTextColor = seed => {
  let hash = 0
  const value = String(seed || "")
  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash % 360)
  return `hsl(${hue}, 90%, 40%)`
}

const getRandomBorderColor = seed => {
  let hash = 0
  const value = String(seed || "")
  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash % 360)
  return `hsl(${hue}, 85%, 55%)`
}

const getRandomColor = seed => {
  let hash = 0
  const value = String(seed || "")
  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash % 360)
  return `hsl(${hue}, 85%, 90%)`
}

/**
 * Archived projects block for My Profile / Projects listing Archive tab.
 * List: GET /api/v1/project-backups/archive
 * Restore / permanent delete use the archive row (project-member) id.
 *
 * @param {object} [props]
 * @param {boolean} [props.embedded] - when true, omit section title (used under Projects tabs)
 * @param {function} [props.onRestored] - called after a successful restore
 */
const ArchivedProjectsListing = ({ embedded = false, onRestored } = {}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("")
  const [sortBy, setSortBy] = useState("created_at")
  const [sortDirection, setSortDirection] = useState("desc")
  const [currentPage, setCurrentPage] = useState(1)
  const [projects, setProjects] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(ARCHIVED_PROJECTS_PAGE_SIZE)
  const [isLoading, setIsLoading] = useState(true)
  const [categories, setCategories] = useState([])
  const [reactivatingId, setReactivatingId] = useState(null)
  const [reactivateTarget, setReactivateTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [tooltipContent, setTooltipContent] = useState(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim())
      setCurrentPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    let cancelled = false
    const loadCategories = async () => {
      try {
        const response = await get(endpoints.getCategories)
        if (!cancelled && response?.data?.success) {
          setCategories(
            (response.data.result || []).map(item => ({
              value: item.id,
              label: item.name,
            })),
          )
        }
      } catch {
        if (!cancelled) setCategories([])
      }
    }
    loadCategories()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    const loadArchived = async () => {
      setIsLoading(true)
      const result = await fetchArchivedProjects({
        page: currentPage,
        pageSize: ARCHIVED_PROJECTS_PAGE_SIZE,
        search: debouncedSearch,
        categoryIds: selectedCategory ? [selectedCategory] : [],
        sortBy,
        sortDirection,
        signal: controller.signal,
      })

      if (controller.signal.aborted || result?.aborted) return

      setProjects(result.list || [])
      setTotalItems(result.total || 0)
      setPageSize(result.perPage || ARCHIVED_PROJECTS_PAGE_SIZE)
      setIsLoading(false)

      const lastPage = Math.max(1, result.lastPage || 1)
      if (currentPage > lastPage) setCurrentPage(lastPage)
    }

    loadArchived()
    return () => {
      controller.abort()
    }
  }, [currentPage, debouncedSearch, selectedCategory, sortBy, sortDirection])

  const refreshList = async () => {
    setIsLoading(true)
    const result = await fetchArchivedProjects({
      page: currentPage,
      pageSize: ARCHIVED_PROJECTS_PAGE_SIZE,
      search: debouncedSearch,
      categoryIds: selectedCategory ? [selectedCategory] : [],
      sortBy,
      sortDirection,
    })
    setProjects(result.list || [])
    setTotalItems(result.total || 0)
    setPageSize(result.perPage || ARCHIVED_PROJECTS_PAGE_SIZE)
    setIsLoading(false)
  }

  const handleTooltipEnter = (event, content) => {
    const rect = event.currentTarget.getBoundingClientRect()
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    })
    setTooltipContent(content)
  }

  const handleTooltipLeave = () => {
    setTooltipContent(null)
  }

  const handleReactivateConfirm = async () => {
    if (!reactivateTarget?.id || reactivatingId) return

    setReactivatingId(reactivateTarget.id)
    try {
      const result = await restoreArchivedProject(reactivateTarget.id)
      if (!result.ok) {
        toastApiError(result.response, "Failed to restore project")
        return
      }
      toast.success(
        result.response?.data?.message || "Project restored",
        TOAST_OPTIONS,
      )
      setReactivateTarget(null)
      await refreshList()
      onRestored?.()
    } finally {
      setReactivatingId(null)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id || isDeleting) return

    setIsDeleting(true)
    try {
      const result = await permanentlyDeleteArchivedProject(deleteTarget.id)
      if (!result.ok) {
        toastApiError(result.response, "Failed to permanently delete project")
        return
      }
      toast.success(
        result.response?.data?.message || "Project permanently deleted",
        TOAST_OPTIONS,
      )
      setDeleteTarget(null)
      await refreshList()
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <section
      className={`my-profile__block my-profile__archive${
        embedded ? " my-profile__archive--embedded" : ""
      }`}
    >
      <div className="my-profile__block-head my-profile__archive-head">
        <div className="my-profile__archive-titles">
          {embedded ? null : (
            <h3 className="my-profile__block-title">Archived Projects</h3>
          )}
          <p className="my-profile__archive-subtitle">
            Restore or permanently delete projects you&apos;ve archived.
          </p>
        </div>
        <div className="my-profile__archive-controls">
          <SearchInput
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search by Project Title..."
            iconSrc={ICONS.search}
          />
          <FilterDropdown
            filters={[
              {
                key: "category",
                type: "select",
                label: "Category",
                multiple: false,
                searchable: true,
                value: selectedCategory,
                onChange: next => {
                  setSelectedCategory(
                    Array.isArray(next) ? next[0] || "" : next || "",
                  )
                  setCurrentPage(1)
                },
                options: categories,
              },
              {
                key: "sortBy",
                type: "sortBy",
                label: "Sort By",
                value: sortBy,
                defaultValue: "created_at",
                onChange: value => {
                  setSortBy(value)
                  setCurrentPage(1)
                },
                options: ARCHIVED_SORT_OPTIONS,
              },
              {
                key: "sortDirection",
                type: "sortDirection",
                label: "Order",
                value: sortDirection,
                defaultValue: "desc",
                onChange: value => {
                  setSortDirection(value)
                  setCurrentPage(1)
                },
              },
            ]}
          />
        </div>
      </div>

      <div className="projects-listing__table-wrapper my-profile__archive-table-wrap">
        <div
          className="projects-listing__table my-profile__archive-table"
          role="table"
          aria-label="Archived Projects"
          aria-busy={isLoading || undefined}
        >
          <div
            className="projects-listing__table-row projects-listing__table-row--head"
            role="row"
          >
            <div role="columnheader">Project Title</div>
            <div role="columnheader">Category</div>
            <div role="columnheader">Collaborators</div>
            <div role="columnheader">Archived</div>
            <div role="columnheader">Start Date</div>
            <div role="columnheader">End Date</div>
            <div
              className="projects-listing__table-actions-head"
              role="columnheader"
            >
              Actions
            </div>
          </div>

          {isLoading
            ? Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <div
                  key={`archived-skel-${index}`}
                  className="projects-listing__table-row projects-listing__table-row--skeleton"
                  role="row"
                  aria-hidden="true"
                >
                  {Array.from({ length: 7 }).map((__, cellIdx) => (
                    <div
                      key={cellIdx}
                      className="projects-listing__skeleton-cell"
                      role="cell"
                    >
                      <div className="projects-listing__skeleton-bar" />
                    </div>
                  ))}
                </div>
              ))
            : null}

          {!isLoading &&
            projects.map(row => (
              <div
                key={row.id}
                className="projects-listing__table-row"
                role="row"
              >
                <div
                  className="projects-listing__table-cell projects-listing__table-cell--title"
                  role="cell"
                >
                  <span
                    className="projects-listing__table-code"
                    style={{
                      background: getRandomColor(row.title),
                      color: getRandomTextColor(row.title),
                    }}
                  >
                    {row.code}
                  </span>
                  <TruncatedText className="projects-listing__table-title">
                    {row.title}
                  </TruncatedText>
                </div>
                <div className="projects-listing__table-cell" role="cell">
                  {row.categories.length > 0 ? (
                    <div className="projects-listing__table-categories">
                      {row.categories
                        .slice(0, MAX_VISIBLE_CATEGORIES)
                        .map((cat, idx) => (
                          <span
                            key={`${row.id}-cat-${idx}`}
                            className="projects-listing__table-category projects-listing__table-category--chip"
                            style={{
                              background: "transparent",
                              color: getRandomTextColor(cat),
                              borderColor: getRandomBorderColor(cat),
                            }}
                          >
                            {cat}
                          </span>
                        ))}
                      {row.categories.length > MAX_VISIBLE_CATEGORIES ? (
                        <span
                          className="projects-listing__table-category projects-listing__table-category--more"
                          onMouseEnter={event =>
                            handleTooltipEnter(
                              event,
                              row.categories
                                .slice(MAX_VISIBLE_CATEGORIES)
                                .join(", "),
                            )
                          }
                          onMouseLeave={handleTooltipLeave}
                        >
                          +{row.categories.length - MAX_VISIBLE_CATEGORIES}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <span className="projects-listing__table-category">—</span>
                  )}
                </div>
                <div
                  className="projects-listing__table-cell projects-listing__table-cell--center"
                  role="cell"
                >
                  <AvatarGroup
                    people={row.collaborators}
                    size="xs"
                    max={4}
                  />
                </div>
                <div className="projects-listing__table-cell" role="cell">
                  <TruncatedText>{row.archivedDate}</TruncatedText>
                </div>
                <div className="projects-listing__table-cell" role="cell">
                  <TruncatedText>{row.startDate}</TruncatedText>
                </div>
                <div className="projects-listing__table-cell" role="cell">
                  <TruncatedText>{row.endDate}</TruncatedText>
                </div>
                <div
                  className="projects-listing__table-cell projects-listing__table-cell--actions"
                  role="cell"
                >
                  <CustomButton
                    variant="secondary"
                    size="sm"
                    type="button"
                    disabled={reactivatingId === row.id}
                    onClick={() => setReactivateTarget(row)}
                  >
                    Restore
                  </CustomButton>
                  <CustomIconButton
                    iconSrc={ICONS.trash}
                    label="Permanently delete"
                    tone="danger"
                    size="sm"
                    onClick={() => setDeleteTarget(row)}
                  />
                </div>
              </div>
            ))}
        </div>

        {!isLoading && projects.length === 0 ? (
          <EmptyState
            title="No Archived Projects"
            description="Archived projects matching this search will show up here."
          />
        ) : null}
      </div>

      <Pagination
        className="my-profile__archive-pagination"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Projects"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
      />

      <DeleteConfirmation
        isOpen={Boolean(reactivateTarget)}
        onCancel={() => !reactivatingId && setReactivateTarget(null)}
        onConfirm={handleReactivateConfirm}
        title="Restore project"
        message={`Restore “${reactivateTarget?.title || "this project"}” from the archive? It will return to your active projects.`}
        cancelText="Cancel"
        confirmText="Restore"
        confirmLoadingText="Restoring…"
        isDeleting={Boolean(reactivatingId)}
      />

      <DeleteConfirmation
        isOpen={Boolean(deleteTarget)}
        onCancel={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Permanently delete project"
        message={`Permanently delete “${deleteTarget?.title || "this project"}”? This cannot be undone.`}
        cancelText="Cancel"
        confirmText="Delete Forever"
        confirmLoadingText="Deleting…"
        isDeleting={isDeleting}
      />

      {tooltipContent ? (
        <div
          className="projects-listing__tooltip"
          style={{
            position: "fixed",
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: "translate(-50%, -100%)",
            zIndex: 1000,
          }}
        >
          {tooltipContent}
        </div>
      ) : null}
    </section>
  )
}

export default ArchivedProjectsListing
